/**
 * Checkout extension widget display defaults (all surfaces).
 * Compact widgets: single image + dropdown. Post-purchase: hero layout + auto image height + dropdown.
 */
import {
  COVER_IMAGE_LAYOUT,
  PRICE_LAYOUT,
  VARIANT_PICKER_STYLE,
  getCoverImageDisplay,
  getPriceDisplay,
  getVariantPickerDisplay,
} from "./product-block-display.js";
import { EXTENSION_PAGE, getExtensionPageForPopup } from "./extension-preview.js";

/** Fixed 64px thumbnail column — matches live checkout extension list widget. */
export const COMPACT_EXTENSION_COVER_IMAGE_SIZES = Object.freeze({
  desktop: Object.freeze({
    columnWidth: 64,
    mainHeight: 64,
    thumbWidth: 44,
    thumbHeight: 44,
  }),
  mobile: Object.freeze({
    columnWidth: 56,
    mainHeight: 56,
    thumbWidth: 40,
    thumbHeight: 40,
  }),
});

/** Slider card hero image — full card width, square aspect. */
export const EXTENSION_SLIDER_COVER_IMAGE_SIZES = Object.freeze({
  desktop: Object.freeze({
    columnWidth: 340,
    mainHeight: 340,
    thumbWidth: 44,
    thumbHeight: 44,
  }),
  mobile: Object.freeze({
    columnWidth: 280,
    mainHeight: 280,
    thumbWidth: 40,
    thumbHeight: 40,
  }),
});

export const COMPACT_EXTENSION_WIDGET_SURFACES = Object.freeze([
  EXTENSION_PAGE.CHECKOUT,
  EXTENSION_PAGE.THANK_YOU_PAGE,
  EXTENSION_PAGE.ORDER_STATUS_PAGE,
  EXTENSION_PAGE.CART_DRAWER,
]);

export function isCompactExtensionWidgetSurface(surface) {
  return Boolean(
    surface && COMPACT_EXTENSION_WIDGET_SURFACES.includes(surface)
  );
}

export function isCompactExtensionWidgetPopup(popup) {
  return isCompactExtensionWidgetSurface(getExtensionPageForPopup(popup));
}

export function isPostPurchaseExtensionWidgetPopup(popup) {
  return getExtensionPageForPopup(popup) === EXTENSION_PAGE.POST_PURCHASE;
}

export function isExtensionWidgetPopup(popup) {
  return (
    isCompactExtensionWidgetPopup(popup) ||
    isPostPurchaseExtensionWidgetPopup(popup)
  );
}

function applyExtensionDropdownVariant(subsection) {
  const display = getVariantPickerDisplay(subsection);
  return {
    ...subsection,
    setting: {
      ...(subsection.setting || {}),
      variantPickerVariant: VARIANT_PICKER_STYLE.DROPDOWN,
      display: {
        ...display,
        variantPickerVariant: VARIANT_PICKER_STYLE.DROPDOWN,
      },
    },
  };
}

/**
 * @param {object} subsection PRODUCT inner block
 */
export function applyCompactExtensionWidgetSubsectionDefaults(subsection) {
  if (!subsection || typeof subsection !== "object") return subsection;

  if (subsection.type === "VARIANT_PICKER") {
    return applyExtensionDropdownVariant(subsection);
  }

  if (subsection.type === "COVER_IMAGE") {
    const display = getCoverImageDisplay(subsection);
    return {
      ...subsection,
      setting: {
        ...(subsection.setting || {}),
        display: {
          ...display,
          layout: COVER_IMAGE_LAYOUT.SINGLE,
          sizes: {
            desktop: { ...COMPACT_EXTENSION_COVER_IMAGE_SIZES.desktop },
            mobile: { ...COMPACT_EXTENSION_COVER_IMAGE_SIZES.mobile },
          },
        },
      },
    };
  }

  if (subsection.type === "PRICE") {
    const display = getPriceDisplay(subsection);
    return {
      ...subsection,
      setting: {
        ...(subsection.setting || {}),
        display: {
          ...display,
          layout: PRICE_LAYOUT.STACKED_COMPACT,
        },
      },
    };
  }

  return subsection;
}

/**
 * Extension slider cards: full-width square hero image above product details.
 * @param {object} subsection PRODUCT inner block
 */
export function applyExtensionSliderSubsectionDefaults(subsection) {
  if (!subsection || typeof subsection !== "object") return subsection;

  if (subsection.type === "VARIANT_PICKER") {
    return applyExtensionDropdownVariant(subsection);
  }

  if (subsection.type === "COVER_IMAGE") {
    const display = getCoverImageDisplay(subsection);
    return {
      ...subsection,
      setting: {
        ...(subsection.setting || {}),
        display: {
          ...display,
          layout: COVER_IMAGE_LAYOUT.SINGLE,
          autoImageHeight: true,
          sizes: {
            desktop: { ...EXTENSION_SLIDER_COVER_IMAGE_SIZES.desktop },
            mobile: { ...EXTENSION_SLIDER_COVER_IMAGE_SIZES.mobile },
          },
        },
      },
    };
  }

  return subsection;
}

/**
 * Post-purchase: dropdown variants + fluid image height (no manual size overrides).
 * @param {object} subsection PRODUCT inner block
 */
export function applyPostPurchaseExtensionSubsectionDefaults(subsection) {
  if (!subsection || typeof subsection !== "object") return subsection;

  if (subsection.type === "VARIANT_PICKER") {
    return applyExtensionDropdownVariant(subsection);
  }

  if (subsection.type === "COVER_IMAGE") {
    const display = getCoverImageDisplay(subsection);
    const setting = subsection.setting || {};
    const dimension = setting.dimension || {};
    const autoDimension = (view) => ({
      ...(dimension[view] || {}),
      width: dimension[view]?.width ?? "100%",
      height: "auto",
    });
    return {
      ...subsection,
      setting: {
        ...setting,
        dimension: {
          desktop: autoDimension("desktop"),
          mobile: autoDimension("mobile"),
        },
        display: {
          layout: display.layout,
          badge: display.badge,
          autoScroll: display.autoScroll,
          scrollIntervalMs: display.scrollIntervalMs,
          scrollSpeed: display.scrollSpeed,
          gap: display.gap,
          maxThumbnailsVisible: display.maxThumbnailsVisible,
          autoImageHeight: true,
          sizes: {
            desktop: {},
            mobile: {},
          },
        },
      },
    };
  }

  return subsection;
}

/**
 * @param {object} subsection
 * @param {object} popup
 */
export function applyExtensionWidgetSubsectionDefaults(subsection, popup) {
  if (isPostPurchaseExtensionWidgetPopup(popup)) {
    return applyPostPurchaseExtensionSubsectionDefaults(subsection);
  }
  if (isCompactExtensionWidgetPopup(popup)) {
    return applyCompactExtensionWidgetSubsectionDefaults(subsection);
  }
  return subsection;
}
